import { type FC, useEffect } from 'react';
import { useFragment, graphql } from 'react-relay';
import { interactionTypeConstants } from 'dibs-tracking';
import { NavItem } from './NavItem.tsx';
import { NavExpandingAreaItem } from './NavExpandingAreaItem.tsx';
import { ExpandingArea } from 'dibs-elements/exports/ExpandingArea.tsx';
import { trackNavInteraction } from '../../helpers/tracking.ts';
import { useNavContext } from '../../NavContext.tsx';
import { userServerVars } from '../../userServerVars.ts';
import { isMobile } from 'dibs-media-query';

import styles from './styles/NavExpandingAreaItems.scss';
import classnames from 'classnames';
import dibsCss from 'dibs-css';

import { type NavExpandingAreaItems_seller$key } from './__generated__/NavExpandingAreaItems_seller.graphql.ts';
import { type NavExpandingAreaItems_navItem$key } from './__generated__/NavExpandingAreaItems_navItem.graphql.ts';
import { type NavExpandingAreaItems_notifications$key } from './__generated__/NavExpandingAreaItems_notifications.graphql.ts';
import { type NavExpandingAreaItems_viewer$key } from './__generated__/NavExpandingAreaItems_viewer.graphql.ts';

type Props = {
    activeLink: string;
    seller?: NavExpandingAreaItems_seller$key | null;
    navItem: NavExpandingAreaItems_navItem$key;
    notifications?: NavExpandingAreaItems_notifications$key | null;
    viewer?: NavExpandingAreaItems_viewer$key | null;
};

const sellerFragment = graphql`
    fragment NavExpandingAreaItems_seller on Seller {
        ...NavItem_seller
        ...NavExpandingAreaItem_seller
    }
`;

const navItemFragment = graphql`
    fragment NavExpandingAreaItems_navItem on NavBarElement {
        ...NavItem_navItem
        dataTn
        eventName
        ... on NavBarList {
            items {
                ...NavExpandingAreaItem_navItem
                title
            }
        }
    }
`;

const notificationsFragment = graphql`
    fragment NavExpandingAreaItems_notifications on DealerNavBarBadges {
        ...NavItem_notifications
        ...NavExpandingAreaItem_notifications
    }
`;

const viewerFragment = graphql`
    fragment NavExpandingAreaItems_viewer on Viewer {
        ...NavItem_viewer
        ...NavExpandingAreaItem_viewer
    }
`;

export const NavExpandingAreaItems: FC<Props> = ({
    activeLink,
    seller: sellerRef,
    navItem: navItemRef,
    notifications: notificationsRef,
    viewer: viewerRef,
}) => {
    const seller = useFragment(sellerFragment, sellerRef);
    const navItem = useFragment(navItemFragment, navItemRef);
    const notifications = useFragment(notificationsFragment, notificationsRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const { expandedArea, setExpandedArea, status, setStatus } = useNavContext();
    const { isDealer } = userServerVars;
    const isMobileView = isMobile();

    const { eventName, dataTn } = navItem;

    const isAreaExpanded = expandedArea === eventName;

    useEffect(() => {
        if (isAreaExpanded) {
            trackNavInteraction({
                action: `${eventName} dropdown`,
                label: eventName,
                interactionType: interactionTypeConstants.INTERACTION_TYPE_DROP_DOWN,
                isImpression: true,
            });
        }
    }, [isAreaExpanded, eventName, expandedArea]);

    if (!navItem || (isMobileView && status === 'collapsed')) {
        return null;
    }

    return (
        <ExpandingArea
            label={
                <NavItem
                    seller={seller}
                    navItem={navItem}
                    notifications={notifications}
                    viewer={viewer}
                    isExpandingAreaItem
                />
            }
            wrapperClass={classnames(styles.expandingAreaWrapper, {
                [dibsCss.mxSmall]: isMobileView,
                [dibsCss.mxXsmall]: !isMobileView,
            })}
            labelClass={classnames(styles.expandingAreaLabel, {
                [styles.desktopDealerExpandingAreaLabel]: !isMobileView && isDealer,
                [styles.desktopInternalExpandingAreaLabel]: !isMobileView && !isDealer,
            })}
            arrowClass={classnames(dibsCss.mrXsmall, {
                [styles.dealerExpandingAreaArrow]: isDealer,
                [styles.internalExpandingAreaArrow]: !isDealer,
                [dibsCss.opacity0]: status === 'collapsed',
                [dibsCss.opacity100]: status === 'expanded' || status === 'overlay',
            })}
            onClick={() => {
                trackNavInteraction({
                    action: eventName,
                    label: eventName,
                    interactionType: interactionTypeConstants.INTERACTION_TYPE_GLOBAL_NAV,
                });
                if (status === 'collapsed') {
                    setStatus('overlay');
                }
                if (isAreaExpanded) {
                    setExpandedArea(null);
                } else {
                    setExpandedArea(eventName);
                }
            }}
            expanded={isAreaExpanded}
            dataTn={dataTn || undefined}
            showArrow
        >
            {navItem.items?.map(item => {
                if (!item) {
                    return null;
                }
                return (
                    <NavExpandingAreaItem
                        key={item.title}
                        seller={seller}
                        navItem={item}
                        notifications={notifications}
                        viewer={viewer}
                        activeLink={activeLink}
                    />
                );
            })}
        </ExpandingArea>
    );
};
